import { useCallback, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { AtSign, Ban, CalendarDays, Link2Off, LogIn, Mail, MailQuestion, RotateCw, TicketCheck, TimerOff, UserRoundCog, Video } from 'lucide-react'
import { SiteHeader } from '@/components/SiteHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { HOST_PERKS, MANAGER_PERKS } from '@/components/hostPerks'
import { Avatar, Button, ButtonLink, Card, EmptyState, Notice, Skeleton, SkeletonGroup, type NoticeTone } from '@/components/play'
import { SignInSheet } from '@/components/watch/SignInSheet'
import { UsernameStep } from '@/components/watch/UsernameStep'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { publicEventPath } from '@/lib/eventLinks'
import { formatEventStart } from '@/lib/explore'
import { acceptFailure, invitesApi, previewFailure, readAcceptedInvitation, readInvitationPreview, stagePath } from '@/lib/invites/api'
import { clearPendingInvite, savePendingInvite } from '@/lib/invites/pendingInvite'
import type { AcceptFailure, GoneInvitationState, InvitationPreview, PreviewFailure } from '@/lib/invites/types'
import { useInviteToken } from '@/lib/invites/useInviteToken'
import { displayNameFor, personToneKey } from '@/lib/profile'
import { waitWords } from '@/lib/retryWait'
import { useAuth } from '@/state/AuthContext'
import '@/components/site-page.css'
import './InvitePage.css'

type Load =
  | { status: 'loading' }
  | { status: 'ready'; preview: InvitationPreview }
  | { status: 'not_found' }
  | { status: 'failed'; failure: Exclude<PreviewFailure, { kind: 'not_found' }> }

const GONE: Record<GoneInvitationState, { icon: ReactElement; title: string }> = {
  expired: { icon: <TimerOff />, title: 'This invite has expired' },
  used: { icon: <TicketCheck />, title: 'This invite has been used' },
  revoked: { icon: <Ban />, title: 'This invite was cancelled' },
}

/**
 * Accepting an invitation (`/invite#<token>`, docs/features/host-watch-along.md, "Accepting an invitation"): who
 * invites you to what, then Accept, which lands on the stage, or for a manager invitation on the event's workspace
 * (docs/features/event-managers.md). A manager invitation names whom it is for: a masked address, an account's @handle
 * (any other account is told so, with Switch account), or nobody for a link, which goes straight to Accept for whoever
 * is signed in. The token comes from the link's fragment, or from this
 * browser's memory of it after signing in (useInviteToken). Signed out, Sign in to accept and the header's Log in open
 * the watch screen's sign-in (Google, Strava or an emailed link, as a viewer), coming back here. A missing username is
 * asked for when accepting needs it. A `.play-site` page on the shared shell, as /host is.
 */
export function InvitePage() {
  useDocumentTitle('Your invite · StreamBridge')
  const { user } = useAuth()
  const token = useInviteToken(user?.id ?? null)
  const [signInOpen, setSignInOpen] = useState(false)
  // An unconfirmed address is confirmed by an emailed link: that sign-in opens on the email field.
  const [emailFirst, setEmailFirst] = useState(false)
  const openSignIn = useCallback(() => {
    setEmailFirst(false)
    setSignInOpen(true)
  }, [])
  const openEmailSignIn = useCallback(() => {
    setEmailFirst(true)
    setSignInOpen(true)
  }, [])

  return (
    <div className="play-theme site-page invite-page play-site">
      <a className="site-page__skip" href="#invite-main">Skip to content</a>
      <SiteHeader onLogIn={openSignIn} />
      <main id="invite-main" className="site-page__main">
        <div className="invite">
          {/* A new link in this tab starts over. */}
          {token ? <Invitation key={token} token={token} onSignIn={openSignIn} onEmailLink={openEmailSignIn} /> : (
            <Missing icon={<MailQuestion />} title="No invite here" description="Open the invite link you were sent." />
          )}
        </div>
      </main>
      <SiteFooter />
      <SignInSheet open={signInOpen} onOpenChange={setSignInOpen} intent={null} emailFirst={emailFirst} />
    </div>
  )
}

/** A state with nothing to accept. The page's heading is for screen readers; the state's own title follows it. */
function Missing({ action, ...state }: { icon: ReactElement; title: string; description: string; action?: ReactNode }) {
  return (
    <>
      <h1 className="sr-only">Your invite</h1>
      <EmptyState {...state} action={action} />
    </>
  )
}

function Invitation({ token, onSignIn, onEmailLink }: { token: string; onSignIn: () => void; onEmailLink: () => void }) {
  const { isAuthenticated, authReady, user, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)
  // A link that can't be accepted any more may be this account's own: a host invitation it took, or a guest link to a
  // stage it is on or hosts. Accepting answers those with the stage, and a gone link with 410, taking nothing. Until
  // that answer the link is kept: signed out, it waits for a sign-in that may show it is theirs.
  const [reopened, setReopened] = useState(false)
  const reopening = useRef(false)

  useEffect(() => {
    let cancelled = false
    invitesApi
      .preview(token)
      .then((body) => {
        if (cancelled) return
        const preview = readInvitationPreview(body)
        if (!preview) return setLoad({ status: 'failed', failure: { kind: 'failed', offline: false } })
        setLoad({ status: 'ready', preview })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const failure = previewFailure(error)
        if (failure.kind === 'not_found') {
          clearPendingInvite()
          setLoad({ status: 'not_found' })
        } else setLoad({ status: 'failed', failure })
      })
    return () => {
      cancelled = true
    }
  }, [token, attempt])

  const gone = load.status === 'ready' && load.preview.state !== 'open'
  const signedIn = isAuthenticated && authReady && Boolean(user)
  useEffect(() => {
    if (!gone || !signedIn || reopening.current) return
    // Once, and not cancelled on cleanup: a second run (React's strict mode) would otherwise drop the answer.
    reopening.current = true
    invitesApi
      .accept(token)
      .then((body) => {
        const accepted = readAcceptedInvitation(body)
        if (accepted) {
          clearPendingInvite()
          return navigate(accepted.path, { replace: true })
        }
        setReopened(true)
      })
      .catch((error: unknown) => {
        const failure = acceptFailure(error)
        // A manager invitation this account took: back to the event's workspace, with the account as it is now (a stale
        // one, still a viewer's, would be sent to start hosting).
        if (failure.kind === 'already_manager' && failure.path) {
          clearPendingInvite()
          const path = failure.path
          return void refreshUser().then(() => navigate(path, { replace: true }))
        }
        // Gone for this account too, or unknown: nothing to come back to. Anything else may work next time.
        if (failure.kind === 'gone' || failure.kind === 'not_found') clearPendingInvite()
        if (failure.kind === 'gone') {
          setLoad((current) => (current.status === 'ready' ? { status: 'ready', preview: { ...current.preview, state: failure.state } } : current))
        }
        setReopened(true)
      })
  }, [gone, signedIn, token, navigate, refreshUser])

  if (load.status === 'loading') return <InviteSkeleton />
  if (load.status === 'not_found') return <NotFound />
  if (load.status === 'failed') {
    return (
      <LoadFailed
        failure={load.failure}
        onRetry={() => {
          setLoad({ status: 'loading' })
          setAttempt((count) => count + 1)
        }}
      />
    )
  }
  const { preview } = load
  if (preview.state !== 'open') {
    // The account loads, or the link is checked against it, before saying the link is gone.
    if ((isAuthenticated && !authReady) || (signedIn && !reopened)) return <InviteSkeleton />
    return <Gone preview={preview} state={preview.state} onSignIn={signedIn ? null : onSignIn} />
  }
  return (
    <OpenInvite
      token={token}
      preview={preview}
      onSignIn={onSignIn}
      onEmailLink={onEmailLink}
      onGone={(state) => setLoad({ status: 'ready', preview: { ...preview, state } })}
      onNotFound={() => setLoad({ status: 'not_found' })}
    />
  )
}

function InviteSkeleton() {
  return (
    <SkeletonGroup label="Loading your invite" className="invite-skeleton">
      <Skeleton shape="circle" className="invite-skeleton__avatar" />
      <Skeleton className="invite-skeleton__title" />
      <Skeleton className="invite-skeleton__line" />
      <Skeleton shape="button" />
    </SkeletonGroup>
  )
}

function NotFound() {
  return <Missing icon={<Link2Off />} title="We couldn’t find this invite" description="Check the link, or ask for a new one." />
}

function LoadFailed({ failure, onRetry }: { failure: Exclude<PreviewFailure, { kind: 'not_found' }>; onRetry: () => void }) {
  let message = 'Try again in a moment.'
  if (failure.kind === 'throttled') {
    const wait = waitWords(failure.retryAfterMs)
    message = wait ? `Too many tries. Try again in ${wait}.` : 'Too many tries. Try again shortly.'
  } else if (failure.offline) message = 'Check your connection and try again.'
  return (
    <>
      <h1 className="sr-only">Your invite</h1>
      <Notice
        tone="error"
        title="We couldn’t load this invite"
        action={<Button variant="secondary" onClick={onRetry}><RotateCw aria-hidden="true" />Try again</Button>}
      >
        {message}
      </Notice>
    </>
  )
}

/**
 * An invitation nobody can accept any more: why, who to ask, and the event, which can still be watched. Signed out,
 * someone who may have taken it (a used host invitation, or a guest link, which also leads its stage's hosts and
 * guests on) can sign in: back here, the link is checked against their account (Invitation).
 */
function Gone({ preview, state, onSignIn }: { preview: InvitationPreview; state: GoneInvitationState; onSignIn: (() => void) | null }) {
  const host = preview.stage_host.name
  const description = state === 'used' && preview.kind === 'host'
    ? `Each invite works once. Ask ${host} for a new one.`
    : `Ask ${host} for a new link.`
  const eventPath = publicEventPath({ id: preview.event.id, attributes: preview.event })
  const mayBeTheirs = preview.kind === 'guest' || state === 'used'
  return (
    <Missing
      {...GONE[state]}
      description={description}
      action={(
        <>
          <ButtonLink variant="secondary" to={eventPath}>Open the event</ButtonLink>
          {onSignIn && mayBeTheirs && <Button variant="ghost" onClick={onSignIn}>Took it? Sign in</Button>}
        </>
      )}
    />
  )
}

function OpenInvite({
  token,
  preview,
  onSignIn,
  onEmailLink,
  onGone,
  onNotFound,
}: {
  token: string
  preview: InvitationPreview
  onSignIn: () => void
  onEmailLink: () => void
  onGone: (state: GoneInvitationState) => void
  onNotFound: () => void
}) {
  const { isAuthenticated, authReady, user, logout, refreshUser } = useAuth()
  const navigate = useNavigate()
  const titleId = useId()
  const [accepting, setAccepting] = useState(false)
  // Set from the first tap until the answer, and kept once it is a stage: a second tap sends nothing.
  const busy = useRef(false)
  const [problem, setProblem] = useState<AcceptFailure | null>(null)
  const [usernameOpen, setUsernameOpen] = useState(false)
  const { event, kind, stage_host: host } = preview

  const accept = useCallback(async () => {
    if (busy.current) return
    busy.current = true
    setAccepting(true)
    setProblem(null)
    let failure: AcceptFailure
    try {
      const accepted = readAcceptedInvitation(await invitesApi.accept(token))
      if (accepted) {
        clearPendingInvite()
        // A manager may now open the workspace, which a viewer's account can't: load the account as it is now first.
        if (accepted.kind === 'manager') await refreshUser()
        // The invite is spent: Back from the stage skips it.
        navigate(accepted.path, { replace: true })
        return
      }
      failure = { kind: 'failed', offline: false }
    } catch (error) {
      failure = acceptFailure(error)
    }
    // Its workspace is this account's already: load the account as it is now, so Open the workspace goes straight in.
    if (failure.kind === 'already_manager') await refreshUser()
    busy.current = false
    setAccepting(false)
    if (failure.kind === 'gone' || failure.kind === 'not_found') {
      clearPendingInvite()
      if (failure.kind === 'gone') onGone(failure.state)
      else onNotFound()
      return
    }
    if (failure.kind === 'username_required') return setUsernameOpen(true)
    // The session is no longer valid: sign in again, here.
    if (failure.kind === 'signed_out') logout()
    setProblem(failure)
  }, [logout, navigate, onGone, onNotFound, refreshUser, token])

  /** Another account should take it: sign out, keep the link for whoever signs in next, and open sign-in. */
  const switchAccount = useCallback(() => {
    logout()
    // Signing out forgets the pending invite; this person is handing it on, so it is kept, unclaimed.
    savePendingInvite(token)
    setProblem(null)
    onSignIn()
  }, [logout, onSignIn, token])

  const title = kind === 'host'
    ? `${host.name} invites you to host a stage at ${event.name}`
    : kind === 'manager'
      ? `${preview.organization_name ?? host.name} invites you to help run ${event.name}`
      : `Join ${host.name}’s stage at ${event.name}`
  const startsAt = event.starts_at ? formatEventStart(event.starts_at) : ''
  const final = problem !== null && FINAL.includes(problem.kind)

  return (
    <Card as="article" className="invite-card" aria-labelledby={titleId}>
      {/* A host or manager invitation's stage_host is the event's organization: the brand's yellow until its logo shows.
          A guest link's could be a person's or an organization's stage (the preview doesn't say), so it keeps the
          neutral disc. */}
      <Avatar name={host.name} src={host.avatar_url} size="large" tone={kind === 'guest' ? 'neutral' : 'organization'} />
      <h1 id={titleId} className="invite-card__title">{title}</h1>
      {startsAt && (
        <p className="invite-card__meta">
          <CalendarDays aria-hidden="true" />
          <time dateTime={event.starts_at ?? undefined}>{startsAt}</time>
        </p>
      )}
      {/* A manager invitation for one address or account says which (the address masked), so the right account signs
          in. A link is for whoever opens it. */}
      {preview.email_hint && (
        <p className="invite-card__meta">
          <Mail aria-hidden="true" />
          <span>For {preview.email_hint}</span>
        </p>
      )}
      {preview.handle_hint && (
        <p className="invite-card__meta">
          <AtSign aria-hidden="true" />
          <span>For {preview.handle_hint}</span>
        </p>
      )}

      {kind === 'manager' ? (
        <div className="invite-card__what">
          {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
          <ul className="invite-perks" role="list" aria-label="What you’ll run">
            {MANAGER_PERKS.map(({ icon: Icon, label }) => (
              <li key={label} className="invite-perks__item">
                <span className="invite-perks__glyph"><Icon aria-hidden="true" /></span>
                {label}
              </li>
            ))}
          </ul>
        </div>
      ) : kind === 'host' ? (
        <section className="invite-card__what" aria-labelledby={`${titleId}-what`}>
          <p id={`${titleId}-what`} className="invite-card__line">Your own stage on the event page.</p>
          {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
          <ul className="invite-perks" role="list" aria-labelledby={`${titleId}-what`}>
            {HOST_PERKS.map(({ icon: Icon, label }) => (
              <li key={label} className="invite-perks__item">
                <span className="invite-perks__glyph"><Icon aria-hidden="true" /></span>
                {label}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="invite-card__line invite-card__line--icon">
          <Video aria-hidden="true" />
          Check your camera and mic, then join.
        </p>
      )}

      <div className="invite-card__actions">
        {isAuthenticated && !authReady ? (
          <SkeletonGroup label="Loading your account">
            <Skeleton shape="button" />
          </SkeletonGroup>
        ) : user && isAuthenticated ? (
          <>
            {!final && <Button onClick={() => void accept()} loading={accepting}>Accept</Button>}
            <p className="invite-card__as">
              <Avatar name={displayNameFor(user)} src={user.attributes.avatar_url} size="xs" tone="auto"
                toneKey={personToneKey({ handle: user.attributes.username, id: user.id })} />
              Signed in as {displayNameFor(user)}
            </p>
          </>
        ) : (
          <Button onClick={onSignIn}><LogIn aria-hidden="true" />Sign in to accept</Button>
        )}
      </div>

      {problem && (
        <Problem problem={problem} preview={preview} final={final} onSwitchAccount={switchAccount} onEmailLink={onEmailLink} />
      )}

      <UsernameStep
        open={usernameOpen}
        onOpenChange={setUsernameOpen}
        onDone={() => {
          setUsernameOpen(false)
          void accept()
        }}
      />
    </Card>
  )
}

/** Accepting again can't change these answers, so Accept goes and focus moves to the answer; for the others Accept
 *  stays, and is the retry (a stage's seat frees, a chat timeout ends). */
const FINAL: readonly AcceptFailure['kind'][] = ['already_host', 'already_manager', 'wrong_account', 'email_unconfirmed', 'refused']

/**
 * Where your stage is, for someone told they have one: a member of the owner organization hosts its stage (the host
 * invitation's `stage_host` is that organization), anyone else their own, by their username.
 */
function useOwnStagePath(preview: InvitationPreview) {
  const { user, organization } = useAuth()
  const member = preview.kind === 'host' && organization?.attributes.slug === preview.stage_host.handle
  return stagePath(preview.event, member ? null : user?.attributes.username)
}

/** Why Accept didn't work, and what to do. A final answer takes focus, as the Accept it replaces had it. */
function Problem({
  problem,
  preview,
  final,
  onSwitchAccount,
  onEmailLink,
}: {
  problem: AcceptFailure
  preview: InvitationPreview
  final: boolean
  onSwitchAccount: () => void
  /** Opens the sign-in on an emailed link, which confirms the address; the invite stays for when it's done. */
  onEmailLink: () => void
}) {
  const { event, stage_host: host } = preview
  const ownStage = useOwnStagePath(preview)
  const notice = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (final) notice.current?.focus()
  }, [final, problem])

  let tone: NoticeTone = 'error'
  let message: string
  let action: ReactNode = null
  switch (problem.kind) {
    case 'already_host':
      tone = 'info'
      message = `You already have a stage at ${event.name}.`
      action = <ButtonLink variant="secondary" to={ownStage}>Open your stage</ButtonLink>
      break
    case 'already_manager':
      tone = 'info'
      message = `You already help run ${event.name}.`
      action = <ButtonLink variant="secondary" to={problem.path ?? `/events/${encodeURIComponent(event.id)}`}>Open the workspace</ButtonLink>
      break
    case 'wrong_account':
      tone = 'warning'
      message = problem.handleHint
        ? `This invite is for ${problem.handleHint}.`
        : problem.emailHint
          ? `This invite is for ${problem.emailHint}. Sign in with that address.`
          : 'This invite is for another account. Sign in with that one.'
      action = <Button variant="secondary" onClick={onSwitchAccount}><UserRoundCog aria-hidden="true" />Switch account</Button>
      break
    case 'email_unconfirmed':
      tone = 'warning'
      message = problem.emailHint
        ? `Confirm ${problem.emailHint} first: sign in once with an email link to it.`
        : 'Confirm your email address first: sign in once with an email link to it.'
      action = <Button variant="secondary" onClick={onEmailLink}><Mail aria-hidden="true" />Email me a link</Button>
      break
    case 'stage_full':
      tone = 'warning'
      message = `${host.name}’s stage is full. Try again when a seat frees up.`
      break
    case 'chat_blocked':
      tone = 'warning'
      message = 'You can’t join this stage while you’re blocked from its chat.'
      break
    case 'already_on_stage':
      tone = 'warning'
      message = `You’re on another stage of ${event.name}. Leave it, then try again.`
      break
    case 'signed_out':
      message = 'Your session ended. Sign in again.'
      break
    case 'refused':
      message = 'This account can’t accept invites.'
      break
    case 'throttled': {
      const wait = waitWords(problem.retryAfterMs)
      message = wait ? `Too many tries. Try again in ${wait}.` : 'Too many tries. Try again shortly.'
      break
    }
    default:
      message = problem.kind === 'failed' && problem.offline
        ? 'We couldn’t accept the invite. Check your connection and try again.'
        : 'We couldn’t accept the invite. Try again.'
  }
  return tone === 'error'
    ? <Notice ref={notice} tabIndex={-1} tone="error" alert action={action} className="invite-card__problem">{message}</Notice>
    : <Notice ref={notice} tabIndex={-1} tone={tone} action={action} className="invite-card__problem">{message}</Notice>
}
